import { useAuth } from "@/auth/auth";
import { useLocalStorage } from "@/auth/localStorage.tsx";
import { useUser } from "@/client/backend/user.tsx";
import type { components } from "@/client/backend/zz_schema";
import useSWRMutation from "swr/mutation";
import useSWR from "swr";

export function useUpdateOrganization(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}` : null,
    (_, { arg }: { arg: components["schemas"]["OrganizationMutable"] }) => {
      if (organizationId === "loading") {
        console.log("cannot update organization while it is still loading");
        return undefined;
      }
      return auth.client.PATCH("/organizations/{organizationId}", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useCreateOrganization() {
  const auth = useAuth();

  return useSWRMutation(
    "/organizations",
    (_, { arg }: { arg: components["schemas"]["OrganizationCreate"] }) => {
      return auth.client.POST("/organizations", {
        body: arg,
      });
    },
  );
}

export function useOrganizationMembers(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/members` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/members", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function useAddOrganizationMember(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/members` : null,
    (_, { arg }: { arg: { emailAddress: string } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.PUT("/organizations/{organizationId}/members", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useRemoveOrganizationMember(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/members` : null,
    (_, { arg }: { arg: { emailAddress: string } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.DELETE("/organizations/{organizationId}/members", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useSendSupportMessage(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/support` : null,
    (_, { arg }: { arg: components["schemas"]["SupportMessage"] }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.POST("/organizations/{organizationId}/support", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useInviteUser(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/invitations` : null,
    (_, { arg }: { arg: components["schemas"]["InvitationRequest"] }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.POST("/organizations/{organizationId}/invitations", {
        params: {
          path: {
            organizationId,
          },
        },
        body: arg,
      });
    },
  );
}

export function useAcceptInvitation() {
  const auth = useAuth();

  return useSWRMutation(
    `/organizations/{organizationId}/invitations/accept`,
    (_, { arg }: { arg: { organizationId: string } }) => {
      return auth.client.POST("/organizations/{organizationId}/invitations/accept", {
        params: {
          path: {
            organizationId: arg.organizationId,
          },
        },
      });
    },
  );
}

export function useDeclineInvitation() {
  const auth = useAuth();

  return useSWRMutation(
    `/organizations/{organizationId}/invitations/decline`,
    (_, { arg }: { arg: { organizationId: string } }) => {
      return auth.client.POST("/organizations/{organizationId}/invitations/decline", {
        params: {
          path: {
            organizationId: arg.organizationId,
          },
        },
      });
    },
  );
}

export function useGetBillingSession(organizationId: string | "loading", emailAddress?: string) {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/billing` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/billing", {
        params: {
          path: {
            organizationId,
          },
          query: emailAddress ? { emailAddress } : {},
        },
      });
    },
  );
}

export function useGetCheckoutSession(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId !== "loading" ? `/organizations/${organizationId}/checkout` : null,
    (_, { arg }: { arg: { priceId: string } }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/checkout", {
        params: {
          path: {
            organizationId,
          },
          query: {
            priceId: arg.priceId,
          },
        },
      });
    },
  );
}

export function useSubscriptions(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWR(
    organizationId !== "loading" ? `/organizations/${organizationId}/subscriptions` : null,
    () => {
      if (organizationId === "loading") return undefined;
      return auth.client.GET("/organizations/{organizationId}/subscriptions", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    },
  );
}

export function useOrganization(): [
  components["schemas"]["Organization"] | "loading",
  (organization: components["schemas"]["Organization"]) => void,
] {
  const [storedOrganization, setStoredOrganization] = useLocalStorage("organization");
  const [,setOrganizationTid] = useLocalStorage("organizationTid");
  const { user } = useUser();

  const setOrganization = (
    organization: components["schemas"]["Organization"],
  ) => {
    setOrganizationTid(organization.tid);
    setStoredOrganization(JSON.stringify(organization));
  };

  // If user data hasn't loaded yet, but we have a stored organization, return that
  if (!user || user === "loading") {
    if (storedOrganization) {
      return [
        JSON.parse(storedOrganization) as components["schemas"]["Organization"],
        setOrganization,
      ];
    }
    return ["loading", setOrganization];
  }

  // Once we have user data, try to match the stored organization with user's organizations
  if (storedOrganization) {
    try {
      const parsed = JSON.parse(
        storedOrganization,
      ) as components["schemas"]["Organization"];
      const matched = user.organizations.find((o) => o.id === parsed.id);
      if (matched) {
        return [matched, setOrganization];
      }
    } catch {
      // fallthrough to default org
    }
  }

  // Fallback to user's default organization and persist it
  setOrganization(user.defaultOrganization);
  return [user.defaultOrganization, setOrganization];
}
